import { Capacitor } from '@capacitor/core';
import { SafeArea } from 'capacitor-plugin-safe-area';

import { isAndroid } from '~common/device';

import { getAndroidVersion } from './device';

type SafeAreaInsets = { top: number; right: number; bottom: number; left: number };
type SafeAreaInsetKey = keyof SafeAreaInsets;

export const ANDROID_EDGE_TO_EDGE_VERSION_THRESHOLD = 15;

/**
 * safe area service that sets CSS variables for use in LESS files and unocss custom classes
 */
class SafeAreaService {
  private isInitialized = false;
  private originalInsets: SafeAreaInsets = { top: 0, right: 0, bottom: 0, left: 0 };
  private overriddenInsets: Partial<SafeAreaInsets> = {};

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    // default values for web platforms
    if (!Capacitor.isNativePlatform()) {
      await this.setSafeAreaVariables({ top: 0, right: 0, bottom: 0, left: 0 });
      this.isInitialized = true;
      return;
    }

    try {
      const { insets } = await SafeArea.getSafeAreaInsets();
      this.originalInsets = { ...insets };
      await this.setSafeAreaVariables(insets);
      console.info('[SafeArea] Initialized with insets:', insets);

      await SafeArea.addListener('safeAreaChanged', async (data) => {
        this.originalInsets = { ...data.insets };
        const finalInsets = { ...data.insets, ...this.overriddenInsets };
        await this.setSafeAreaVariables(finalInsets);
        console.info('[SafeArea] Updated insets:', data.insets);
      });

      this.isInitialized = true;
    } catch (error) {
      console.error('[SafeArea] Failed to initialize:', error);
      await this.setSafeAreaVariables({ top: 0, right: 0, bottom: 0, left: 0 });
      this.isInitialized = true;
    }
  }

  private async setSafeAreaVariables(insets: SafeAreaInsets): Promise<void> {
    const root = document.documentElement;
    const androidVersion = await getAndroidVersion();
    const isAndroidOlderThan15 = androidVersion !== null && androidVersion < ANDROID_EDGE_TO_EDGE_VERSION_THRESHOLD;

    const bottomInset = isAndroid() ? 0 : insets.bottom;
    const topInset = isAndroidOlderThan15 ? 0 : insets.top;

    root.style.setProperty('--safe-area-inset-top', `${topInset}px`);
    root.style.setProperty('--safe-area-inset-right', `${insets.right}px`);
    root.style.setProperty('--safe-area-inset-bottom', `${bottomInset}px`);
    root.style.setProperty('--safe-area-inset-left', `${insets.left}px`);
  }

  /**
   * overrides a specific safe area inset with a custom value
   *
   * @param insetKey - The inset to override ('top', 'right', 'bottom', 'left')
   * @param value - The value to set (in pixels)
   */
  public async overrideInset(insetKey: SafeAreaInsetKey, value: number): Promise<void> {
    const root = document.documentElement;
    root.style.setProperty(`--safe-area-inset-${insetKey}`, `${value}px`);
  }

  /**
   * restores a specific safe area inset to its original value
   *
   * @param insetKey - The inset to restore ('top', 'right', 'bottom', 'left')
   */
  public async restoreInset(insetKey: SafeAreaInsetKey): Promise<void> {
    delete this.overriddenInsets[insetKey];

    const finalInsets = { ...this.originalInsets, ...this.overriddenInsets };
    await this.setSafeAreaVariables(finalInsets);
  }

  /**
   * restores all safe area insets to their original values
   */
  public async restoreAllInsets(): Promise<void> {
    this.overriddenInsets = {};
    await this.setSafeAreaVariables(this.originalInsets);
  }

  /**
   * returns the actual device safe area insets
   */
  public getDeviceInsets(): SafeAreaInsets {
    return { ...this.originalInsets };
  }

}

export default new SafeAreaService();
